import { useState } from 'react';

export default function Contact() {
  const [formData, setFormData] = useState({
    nombre: '',
    clinica: '',
    email: '',
    telefono: '',
    tipoConsulta: '',
    mensaje: ''
  });

  const [files, setFiles] = useState<FileList | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (formData.mensaje.length > 500) {
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      if (files && files.length > 0) {
        formBody.append('archivos', 'Uncollectable');
      }

      const response = await fetch('https://readdy.ai/api/public/form/submit/pabloconejerocirugiaveterinaria-contacto', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          nombre: '',
          clinica: '',
          email: '',
          telefono: '',
          tipoConsulta: '',
          mensaje: ''
        });
        setFiles(null);
        const fileInput = document.getElementById('archivos') as HTMLInputElement;
        if (fileInput) fileInput.value = '';
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setFiles(e.target.files);
    }
  };

  return (
    <section id="contacto" className="py-20 bg-gradient-to-b from-white to-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="inline-block bg-accent-100 text-accent-800 px-4 py-2 rounded-full text-sm font-semibold mb-6">
            Contacto
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>
            Contacta Conmigo
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            ¿Necesitas una segunda opinión o tienes un caso complejo? Estoy aquí para colaborar contigo
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Contact Info - Izquierda con tamaño reducido */}
          <div className="lg:col-span-1 space-y-6">
            {/* Direct Contact */}
            <div className="bg-gradient-to-br from-accent-600 to-accent-700 rounded-xl shadow-lg p-6 text-white">
              <h3 className="text-lg font-bold mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
                Contacto Directo
              </h3>

              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-mail-line text-lg"></i>
                  </div>
                  <div>
                    <div className="font-semibold text-sm mb-1">Email</div>
                    <a
                      href="mailto:vetconejero@gmail.com"
                      className="text-sm text-white/90 hover:text-white transition-colors cursor-pointer break-all"
                    >
                      vetconejero@gmail.com
                    </a>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-whatsapp-line text-lg"></i>
                  </div>
                  <div>
                    <div className="font-semibold text-sm mb-1">Teléfono (WhatsApp)</div>
                    <a
                      href="https://wa.me/34600581975"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-white/90 hover:text-white transition-colors cursor-pointer"
                    >
                      +34 600 581 975
                    </a>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-map-pin-line text-lg"></i>
                  </div>
                  <div>
                    <div className="font-semibold text-sm mb-1">Área de Servicio</div>
                    <div className="text-sm text-white/90">
                      <strong>Almería</strong>, Aguadulce, Roquetas de Mar, El Ejido, Almerimar, Adra, Nijar, Carboneras
                    </div>
                    <div className="text-xs text-white/70 mt-1">
                      Y toda la provincia de <strong>Almería</strong>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Map */}
            <div className="bg-white rounded-xl shadow-lg overflow-hidden">
              <div className="p-4 border-b border-gray-200">
                <h3 className="text-base font-bold text-gray-900" style={{ fontFamily: "'Playfair Display', serif" }}>
                  Zona de Cobertura - <strong>Almería</strong>
                </h3>
                <p className="text-xs text-gray-600 mt-1">
                  Servicio móvil de <strong>cirugía veterinaria</strong> en toda la provincia
                </p>
              </div>
              <div className="w-full h-64">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d404181.3634!2d-2.8!3d36.84!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0xd7a9dfd19c2d8b7%3A0x40463fd8ca378f0!2sAlmer%C3%ADa!5e0!3m2!1ses!2ses!4v1234567890"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Mapa de Almería - Zona de cobertura de cirugía veterinaria"
                ></iframe>
              </div>
            </div>

            {/* Social Links */}
            <div className="bg-white rounded-xl shadow-lg p-6">
              <h3 className="text-base font-bold text-gray-900 mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>
                Sígueme en Redes Sociales
              </h3>
              <div className="flex space-x-3">
                <a
                  href="https://www.linkedin.com/in/pabloconejero/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 bg-gradient-to-br from-accent-100 to-accent-200 hover:from-accent-600 hover:to-accent-700 text-accent-600 hover:text-white rounded-lg flex items-center justify-center transition-all duration-300 hover:scale-110 cursor-pointer"
                  aria-label="LinkedIn - Pablo Conejero Cirujano Veterinario"
                >
                  <i className="ri-linkedin-fill text-lg"></i>
                </a>
                <a
                  href="https://www.instagram.com/vetconejero/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 bg-gradient-to-br from-accent-100 to-accent-200 hover:from-accent-600 hover:to-accent-700 text-accent-600 hover:text-white rounded-lg flex items-center justify-center transition-all duration-300 hover:scale-110 cursor-pointer"
                  aria-label="Instagram - Veterinario Especialista Almería"
                >
                  <i className="ri-instagram-fill text-lg"></i>
                </a>
              </div>
            </div>

            {/* Google Business Profile */}
            <div className="bg-white rounded-xl shadow-lg p-6">
              <h3 className="text-base font-bold text-gray-900 mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>
                Encuéntranos en Google
              </h3>
              <a
                href="https://share.google/P15uGxF2dVhsVwi2u"
                target="_blank"
                rel="noopener noreferrer"
                className="block bg-gradient-to-br from-blue-50 to-blue-100 hover:from-blue-100 hover:to-blue-200 rounded-lg p-4 transition-all duration-300 hover:shadow-md cursor-pointer"
              >
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-white rounded-lg flex items-center justify-center flex-shrink-0 shadow-sm">
                    <i className="ri-google-fill text-2xl text-blue-600"></i>
                  </div>
                  <div className="flex-1">
                    <div className="font-semibold text-gray-900 text-sm">Google Business</div>
                    <div className="text-xs text-gray-600 mt-0.5">Ver perfil y reseñas</div>
                  </div>
                  <i className="ri-arrow-right-line text-gray-400"></i>
                </div>
              </a>
              <div className="mt-3 text-xs text-gray-500 text-center">
                <i className="ri-star-fill text-yellow-400"></i> Valoraciones de clientes
              </div>
            </div>
          </div>

          {/* Contact Form - Derecha */}
          <div className="lg:col-span-2 bg-white rounded-2xl shadow-xl p-8 lg:p-10">
            <h3 className="text-2xl font-bold text-gray-900 mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>
              Envía tu Consulta
            </h3>
            <div className="bg-accent-50 border-l-4 border-accent-500 p-4 mb-6">
              <div className="flex items-start space-x-3">
                <i className="ri-information-line text-accent-600 text-xl flex-shrink-0 mt-0.5"></i>
                <div>
                  <div className="font-semibold text-accent-900 text-sm">Importante: Pruebas Diagnósticas</div>
                  <div className="text-sm text-accent-800 mt-1">
                    Para una valoración precisa del caso, es fundamental que adjuntes todas las pruebas diagnósticas disponibles: informes previos, radiografías (Rx), tomografías (TC) y resonancias magnéticas (RM).
                  </div>
                </div>
              </div>
            </div>

            <form id="contacto-form" data-readdy-form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="nombre" className="block text-sm font-semibold text-gray-700 mb-2">
                  Nombre Completo *
                </label>
                <input
                  type="text"
                  id="nombre"
                  name="nombre"
                  value={formData.nombre}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-500 focus:border-transparent transition-all text-sm"
                  placeholder="Dr. Juan Pérez"
                />
              </div>

              <div>
                <label htmlFor="clinica" className="block text-sm font-semibold text-gray-700 mb-2">
                  Clínica/Centro *
                </label>
                <input
                  type="text"
                  id="clinica"
                  name="clinica"
                  value={formData.clinica}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-500 focus:border-transparent transition-all text-sm"
                  placeholder="Clínica Veterinaria..."
                />
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                    Email *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-500 focus:border-transparent transition-all text-sm"
                    placeholder="doctor@clinica.com"
                  />
                </div>

                <div>
                  <label htmlFor="telefono" className="block text-sm font-semibold text-gray-700 mb-2">
                    Teléfono
                  </label>
                  <input
                    type="tel"
                    id="telefono"
                    name="telefono"
                    value={formData.telefono}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-500 focus:border-transparent transition-all text-sm"
                    placeholder="+34 600 000 000"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="tipoConsulta" className="block text-sm font-semibold text-gray-700 mb-2">
                  Tipo de Consulta *
                </label>
                <select
                  id="tipoConsulta"
                  name="tipoConsulta"
                  value={formData.tipoConsulta}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-500 focus:border-transparent transition-all text-sm cursor-pointer"
                >
                  <option value="">Selecciona el tipo de consulta</option>
                  <option value="traumatologia">Traumatología y Ortopedia</option>
                  <option value="oncologia">Oncología Veterinaria</option>
                  <option value="neurocirugia">Neurocirugía</option>
                  <option value="torax-abdomen">Cirugía Torácica y Abdominal</option>
                  <option value="braquicefalico">Síndrome Braquicefálico</option>
                  <option value="otra">Otra Consulta</option>
                </select>
              </div>

              <div>
                <label htmlFor="mensaje" className="block text-sm font-semibold text-gray-700 mb-2">
                  Descripción del Caso *
                </label>
                <textarea
                  id="mensaje"
                  name="mensaje"
                  value={formData.mensaje}
                  onChange={handleChange}
                  required
                  maxLength={500}
                  rows={5}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-500 focus:border-transparent transition-all resize-none text-sm"
                  placeholder="Describe brevemente el caso, síntomas, diagnóstico previo, etc. (máximo 500 caracteres)"
                ></textarea>
                <div className="text-xs text-gray-500 mt-1 text-right">
                  {formData.mensaje.length}/500 caracteres
                </div>
              </div>

              <div>
                <label htmlFor="archivos" className="block text-sm font-semibold text-gray-700 mb-2">
                  Pruebas Diagnósticas (Rx, TC, RM, Informes)
                </label>
                <div className="relative">
                  <input
                    type="file"
                    id="archivos"
                    name="archivos"
                    onChange={handleFileChange}
                    multiple
                    accept=".pdf,.jpg,.jpeg,.png,.dcm"
                    className="hidden"
                  />
                  <label
                    htmlFor="archivos"
                    className="flex items-center justify-center w-full px-4 py-8 border-2 border-dashed border-gray-300 rounded-lg hover:border-accent-500 transition-all cursor-pointer bg-gray-50 hover:bg-accent-50"
                  >
                    <div className="text-center">
                      <i className="ri-upload-cloud-2-line text-4xl text-gray-400 mb-2"></i>
                      <div className="text-sm font-semibold text-gray-700">
                        Haz clic para subir archivos
                      </div>
                      <div className="text-xs text-gray-500 mt-1">
                        Radiografías, TC, RM, informes (PDF, imágenes)
                      </div>
                    </div>
                  </label>
                </div>
                {files && files.length > 0 && (
                  <div className="mt-3 space-y-2">
                    {Array.from(files).map((file, index) => (
                      <div key={index} className="flex items-center space-x-2 text-sm text-gray-600 bg-gray-50 px-3 py-2 rounded">
                        <i className="ri-file-line text-accent-600"></i>
                        <span className="flex-1 truncate">{file.name}</span>
                        <span className="text-xs text-gray-500">{(file.size / 1024).toFixed(1)} KB</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-gradient-to-r from-accent-600 to-accent-700 text-white px-8 py-4 rounded-lg font-semibold hover:from-accent-700 hover:to-accent-800 transition-all duration-300 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2 whitespace-nowrap cursor-pointer"
              >
                {isSubmitting ? (
                  <>
                    <i className="ri-loader-4-line animate-spin"></i>
                    <span>Enviando...</span>
                  </>
                ) : (
                  <>
                    <span>Enviar Consulta</span>
                    <i className="ri-send-plane-fill"></i>
                  </>
                )}
              </button>

              {submitStatus === 'success' && (
                <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg flex items-start space-x-3">
                  <i className="ri-check-line text-xl flex-shrink-0 mt-0.5"></i>
                  <div>
                    <div className="font-semibold">¡Mensaje enviado correctamente!</div>
                    <div className="text-sm mt-1">Te contactaré lo antes posible para valorar tu caso.</div>
                  </div>
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg flex items-start space-x-3">
                  <i className="ri-error-warning-line text-xl flex-shrink-0 mt-0.5"></i>
                  <div>
                    <div className="font-semibold">Error al enviar el mensaje</div>
                    <div className="text-sm mt-1">Por favor, inténtalo de nuevo o contacta por teléfono.</div>
                  </div>
                </div>
              )}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}